import { roomAssetKey } from '../packageOptions/types';
import type { CategoryRoom } from '../packageOptions/types';

import type { ForecastOptionInputState, ForecastSessionState } from './types';

export const DEFAULT_UPGRADE_RATE_PCT = 30;
export const DEFAULT_SOLO_RATE_PCT = 5;

export const MARGIN_PCT_CRITICAL = 15;
export const MARGIN_PCT_WARNING = 20;

export const MIN_VISIBLE_DELTA = 0.05;

export const packageKey = (assetId: string, nights: number) => `${assetId}:${nights}`;

export const toPackageCount = (value: string) => value.replace(/\D.*$/, '');

export const EMPTY_OPTION_INPUTS: ForecastOptionInputState = {
  packagesByAssetLos: {},
  upgradeRatePct: '',
  soloRatePct: '',
  expectedAvgLos: '',
  pctChange: '',
  manualOverride: false,
};

export function createEmptyForecastSession(): ForecastSessionState {
  return { optionInputs: {} };
}

export const parseMoney = (value: string | undefined): number => Number(value) || 0;

export const round2 = (value: number): number => Math.round(value * 100) / 100;

// Resolves per base room by asset key, the way computeForecastTotals does, so a counterpart
// matching no base room reads as inert rather than as live.
export function hasCounterpartForAnyBaseRoom(
  baseRooms: Array<Pick<CategoryRoom, 'assetName' | 'assetSfId'>>,
  counterpartRooms: Array<Pick<CategoryRoom, 'assetName' | 'assetSfId'>>,
): boolean {
  const counterpartKeys = new Set(counterpartRooms.map(roomAssetKey));

  return baseRooms.some((room) => room.assetName && counterpartKeys.has(roomAssetKey(room)));
}

export function parsePercentWithDefault(value: string | undefined, fallback: number): number {
  if (value === undefined || value.trim() === '') {
    return fallback;
  }

  return Number(value) || 0;
}

export function derivePackagesFromPctChange(basePackages: number, pctChangePct: number): number {
  return Math.round(basePackages * (1 + pctChangePct / 100));
}

export function resolveEffectivePackages(
  optionInputs: ForecastOptionInputState,
  optionIndex: number,
  firstOptionInputs: ForecastOptionInputState,
): Record<string, number> {
  const source = optionIndex === 0 || optionInputs.manualOverride ? optionInputs : firstOptionInputs;
  const entries = Object.entries(source.packagesByAssetLos).map(([key, value]) => {
    const packages = parseMoney(value);
    if (source === optionInputs) {
      return [key, packages] as const;
    }

    return [key, derivePackagesFromPctChange(packages, parseMoney(optionInputs.pctChange))] as const;
  });

  return Object.fromEntries(entries);
}
